import { ApiError } from '@estudio/contracts';
import type { CloudClient } from './client.ts';

export interface ChangeEvent {
  kind: 'changes' | 'snapshot';
  entities: { entity: string; entity_id: string; action: string; revision_id: string | null }[];
  cursor: number;
}

export interface CursorStore {
  get(workspaceId: string): number | null;
  set(workspaceId: string, cursor: number): void;
}

export function createLocalCursorStore(storage: Pick<Storage, 'getItem' | 'setItem'> | null = globalThis.localStorage ?? null): CursorStore {
  const key = (ws: string) => `estudio.cursor.${ws}`;
  return {
    get: (ws) => {
      try {
        const v = storage?.getItem(key(ws));
        return v === null || v === undefined ? null : Number(v);
      } catch {
        return null;
      }
    },
    set: (ws, c) => {
      try {
        storage?.setItem(key(ws), String(c));
      } catch {
        // sin almacenamiento: el cursor vive en memoria esta sesión
      }
    },
  };
}

/**
 * Consulta incremental de cambios del espacio con cursor asignado por el
 * servidor. Se ejecuta al abrir, al reconectar, al recuperar foco y como
 * respaldo cada 60 s. Realtime puede acelerar avisos, pero no es la única vía.
 * Si el cursor caducó, descarga una instantánea paginada del índice (nunca vídeos).
 */
export class WorkspaceSync {
  private memCursor: number | null = null;
  private running: Promise<void> | null = null;
  private readonly listeners = new Set<(e: ChangeEvent) => void>();

  constructor(
    private readonly client: CloudClient,
    private readonly workspaceId: string,
    private readonly store: CursorStore = createLocalCursorStore(),
  ) {}

  onChange(listener: (e: ChangeEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get cursor(): number | null {
    return this.store.get(this.workspaceId) ?? this.memCursor;
  }

  private setCursor(c: number): void {
    this.memCursor = c;
    this.store.set(this.workspaceId, c);
  }

  poll(): Promise<void> {
    if (!this.running) this.running = this.run().finally(() => (this.running = null));
    return this.running;
  }

  private async run(): Promise<void> {
    const cursor = this.cursor;
    if (cursor === null) {
      await this.snapshot();
      return;
    }
    try {
      let next = cursor;
      for (let page = 0; page < 20; page++) {
        const res = await this.client.call('document.changes_since', { workspace_id: this.workspaceId, cursor: next, limit: 500 });
        if (res.items.length) {
          this.listeners.forEach((l) =>
            l({ kind: 'changes', cursor: res.next_cursor, entities: res.items.map((i) => ({ entity: i.entity, entity_id: i.entity_id, action: i.action, revision_id: i.revision_id })) }),
          );
        }
        next = res.next_cursor;
        this.setCursor(next);
        if (!res.has_more) break;
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === 'CURSOR_EXPIRED') {
        await this.snapshot();
        return;
      }
      throw e;
    }
  }

  private async snapshot(): Promise<void> {
    let cursor: string | null | undefined;
    let changeCursor = 0;
    const entities: ChangeEvent['entities'] = [];
    for (let page = 0; page < 100; page++) {
      const res = await this.client.call('document.index', { workspace_id: this.workspaceId, cursor: cursor ?? null, limit: 200 });
      if (page === 0) changeCursor = res.change_cursor;
      for (const d of res.items) entities.push({ entity: 'documents', entity_id: String(d.id), action: 'update', revision_id: (d.head_revision_id as string | null) ?? null });
      cursor = res.next_cursor;
      if (!cursor) break;
    }
    this.setCursor(changeCursor);
    this.listeners.forEach((l) => l({ kind: 'snapshot', cursor: changeCursor, entities }));
  }
}
